import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import useComponentMounted from '../../../hooks/useComponentMounted';
import UserContext from '../../../context/UserContext';
import AppCustomization from '../../Model/AppCustomization';
import AppCustomCheckin from '../../Model/AppCustomCheckin';
import AppCustomHabit from '../../Model/AppCustomHabit';
import SecondaryContextsReadyContext from '../SecondaryContextsReadyContext';

import CustomizationContext from './CustomizationContext';

const CustomizationContextProvider = ({
  children,
}) => {
  const [customCheckInCollection, setCustomCheckInCollection] = useState({ docs: [] });
  const [appCustomizationDoc, setAppCustomizationDoc] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [customHabitCollection, setCustomHabitCollection] = useState([]);

  const { readyForRemainingContexts } = useContext(SecondaryContextsReadyContext);
  const {
    userDoc: {
      id: userId,
    },
  } = useContext(UserContext);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const init = async () => {
      if (userId) {
        const appCustomization = await AppCustomization.getAppCustomizationByCoach(userId);
        const checkInCollection = await AppCustomCheckin.getCheckinDocsByCoachId(userId);
        const habitCollection = await AppCustomHabit.getHabitDocsByCoachId(userId);

        if (isComponentMountedRef.current) {
          setAppCustomizationDoc(appCustomization);
          setCustomCheckInCollection(checkInCollection);
          setCustomHabitCollection(habitCollection);
          setIsReady(true);
        }
      }
    };

    // Initialize the context only when the primary and secondary contexts are ready.
    if (!isReady && readyForRemainingContexts) {
      init();
    }
  }, [userId, isReady, isComponentMountedRef, readyForRemainingContexts]);

  const context = useMemo(() => ({
    customHabitCollection,
    appCustomizationDoc,
    customCheckInCollection,
    isReady,
  }), [
    customHabitCollection,
    appCustomizationDoc,
    customCheckInCollection,
    isReady,
  ]);

  return (
    <CustomizationContext.Provider value={context}>
      {children}
    </CustomizationContext.Provider>
  );
};

CustomizationContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(CustomizationContextProvider);
